"use client";

import { ArrowRight, Heart } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AnimalWelfareHero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="513" id="animalwelfarehero" className="relative bg-background text-foreground overflow-hidden min-h-[90vh] flex items-center">
      <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-6 order-2 lg:order-1 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex items-center gap-4 mb-8">
              <span className="font-default text-xs tracking-[0.25em] uppercase text-primary">
                Educational Hub
              </span>
              <span className="h-px w-16 bg-foreground/30" aria-hidden="true" />
            </div>

            {}
            <h1 className="font-default font-bold text-foreground leading-[1.02] text-[2.75rem] sm:text-5xl md:text-6xl lg:text-[5.25rem] tracking-tight mb-8">
              Animal Awareness <span className="italic font-normal text-primary">Library</span>
            </h1>

            {}
            <p className="font-default text-lg leading-relaxed text-muted-foreground max-w-xl mb-10">
              Our comprehensive resource center addresses the critical realities of animal welfare. From the plight of shelter animals and the urgency of rescue missions to the systemic issues of animal cruelty, neglect, and habitat loss, we explore the challenges facing endangered species and the ethics of animal testing.
            </p>

            {}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-default tracking-wide shadow-md h-12 px-7 rounded-lg">
                <Link to="/CurrentCausesPage">
                  Explore Causes
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="text-foreground hover:bg-accent hover:text-accent-foreground font-default tracking-wide h-12 px-7 border border-border/60 rounded-lg">
                <Link to="/ShopPage">
                  <Heart className="mr-1 h-4 w-4" />
                  Support This Cause
                </Link>
              </Button>
            </div>

            {}
            <div className="mt-12 flex items-center gap-6 font-default text-xs tracking-widest uppercase text-muted-foreground">
              <span>Rescue</span>
              <span className="h-px w-8 bg-border" />
              <span>Protection</span>
              <span className="h-px w-8 bg-border" />
              <span>Conservation</span>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 order-1 lg:order-2 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              {}
              <div className="absolute -inset-4 sm:-inset-6 bg-card rounded-lg shadow-xl" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, hsl(155 35% 12% / 0.04) 1px, transparent 1px), radial-gradient(circle at 80% 70%, hsl(155 35% 12% / 0.03) 1px, transparent 1px)",
              backgroundSize: "24px 24px, 32px 32px"
            }} aria-hidden="true" />

              {}
              <div className="relative rounded-lg overflow-hidden shadow-2xl border border-border/40">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/animal_welfare_library_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/animal_welfare_library_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/animal_welfare_library_hero.png-wvc-sizes" alt="A serious and poignant close-up of a rescued animal, highlighting the importance of animal welfare and protection" loading="lazy" decoding="async" className="w-full h-auto aspect-[4/5] object-cover" />

                {}
                <div className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-30" style={{
                backgroundImage: "repeating-linear-gradient(45deg, transparent 0 2px, hsl(155 35% 12% / 0.02) 2px 4px)"
              }} aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}